import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Subscription } from 'rxjs';
import { MoneyService } from '../money.service';
import { Txn, Category, Account, Rule } from '../money.model';
import { derivePattern } from '../rules/rules.engine';
import { recurringKey, Direction } from '../rules/recurring.detector';

export interface TxnFilters {
  month: string;            // 'YYYY-MM' or '' for all
  accountId: string;
  categoryId: string;
  search: string;
  uncategorizedOnly: boolean;
  showTransfers: boolean;
  /**
   * True only for a donut-slice drill-through: the slice is spending only
   * (spendableTxns then amount < 0), so the list behind it must match —
   * excluded rows and non-negative amounts (refunds, income) dropped.
   * Default false everywhere else, including the uncategorized queue, where
   * seeing every row regardless of sign or exclusion is the point.
   */
  spendingOnly: boolean;
}

/** '57.99'- or '$1,234.56'-shaped text as exact cents, or null. Strips the same
 * `[$,\s]` characters as parse-values.ts's parseAmountCents (same reason: a `$`
 * or a comma is a natural thing to type in a search box), but never calls
 * parseAmountCents itself — that throws on ordinary words, and this runs on
 * every keystroke. */
function needleCents(needle: string): number | null {
  const cleaned = needle.replace(/[$,\s]/g, '');
  return /^\d+(\.\d{1,2})?$/.test(cleaned) ? Math.round(Number(cleaned) * 100) : null;
}

function matchesNeedle(t: Txn, needle: string, cents: number | null): boolean {
  if (t.description.toLowerCase().includes(needle)) return true;
  if ((t.merchant ?? '').toLowerCase().includes(needle)) return true;
  if ((t.note ?? '').toLowerCase().includes(needle)) return true;
  return cents !== null && Math.abs(t.amount) === cents;
}

/**
 * The one definition of "uncategorized" for this screen: no category, not a
 * transfer, and not a ghost record (a bulk-categorize patch applied to an id
 * that no longer exists, which has no `date`). Used by filterTxns'
 * uncategorizedOnly branch, uncategorizedByMonth, and the header's
 * reviewCount so the three never drift apart again.
 */
export function isUncategorized(t: Txn): boolean {
  return !!t.date && !t.categoryId && !t.isTransfer;
}

/** Pure — exported for testing without Angular. */
export function filterTxns(txns: Txn[], f: TxnFilters): Txn[] {
  const needle = f.search.trim().toLowerCase();
  const cents = needle ? needleCents(needle) : null;
  return txns
    .filter(t => {
      // Malformed/ghost records (e.g. a bulk-categorize patch applied to an id
      // that no longer exists) have no `date`. Drop them here rather than
      // letting the sort below throw on b.date.localeCompare(...).
      if (!t.date) return false;
      if (t.isTransfer && !f.showTransfers) return false;
      if (f.uncategorizedOnly && !isUncategorized(t)) return false;
      if (f.spendingOnly && (t.excluded || t.amount >= 0)) return false;
      if (f.month && !t.date.startsWith(f.month)) return false;
      if (f.accountId && t.accountId !== f.accountId) return false;
      if (f.categoryId && t.categoryId !== f.categoryId) return false;
      if (needle && !matchesNeedle(t, needle, cents)) return false;
      return true;
    })
    .sort((a, b) => b.date.localeCompare(a.date) || a.id.localeCompare(b.id));
}

/**
 * Uncategorized backlog by month, newest first. Uses the SAME predicate
 * (isUncategorized) as filterTxns' uncategorizedOnly branch and reviewCount
 * so a chip's number always equals the list it opens.
 */
export function uncategorizedByMonth(txns: Txn[]): { month: string; count: number }[] {
  const byMonth = new Map<string, number>();
  (txns ?? []).forEach(t => {
    if (!isUncategorized(t)) return;
    const m = t.date.slice(0, 7);
    byMonth.set(m, (byMonth.get(m) ?? 0) + 1);
  });
  return [...byMonth.entries()].sort(([a], [b]) => b.localeCompare(a))
    .map(([month, count]) => ({ month, count }));
}

export function monthsPresent(txns: Txn[]): string[] {
  // Same ghost-record guard as filterTxns: a record with no `date` cannot be
  // sliced into a month and must not reach it.
  return [...new Set(txns.filter(t => t.date).map(t => t.date.slice(0, 7)))].sort().reverse();
}

export interface RuleOffer { pattern: string; categoryId: string; count: number; }

/** Rows that would be caught by `pattern` and still need a category. */
export function matchingUncategorized(all: Txn[], pattern: string, excludeId?: string): Txn[] {
  const needle = pattern.toUpperCase();
  return all.filter(t =>
    t.id !== excludeId &&
    !t.categoryId &&
    !t.isTransfer &&
    t.matchKey.toUpperCase().includes(needle));
}

/** The offer to make after categorizing `txn`, or null if there is nothing worth offering. */
export function buildRuleOffer(
  txn: Txn, categoryId: string, all: Txn[], rules: Rule[],
): RuleOffer | null {
  const pattern = derivePattern(txn.matchKey);
  if (!pattern) return null;

  const alreadyKnown = rules.some(r => r.pattern.toUpperCase() === pattern.toUpperCase());
  if (alreadyKnown) return null;

  // Exclude the row just categorized — `all` is the last live snapshot and
  // hasn't caught up with the write we just made.
  const count = matchingUncategorized(all, pattern, txn.id).length;
  return { pattern, categoryId, count };
}

/** Append-or-replace by case-insensitive pattern. Returns a new array. */
export function upsertOffer(offers: RuleOffer[], offer: RuleOffer): RuleOffer[] {
  const needle = offer.pattern.toUpperCase();
  const idx = offers.findIndex(o => o.pattern.toUpperCase() === needle);
  if (idx === -1) return [...offers, offer];
  const copy = offers.slice();
  copy[idx] = offer;
  return copy;
}

export interface TransferOffer { pattern: string; count: number; }

/** Rows matching `pattern` that are not already transfers. */
export function matchingNonTransfers(all: Txn[], pattern: string, excludeId?: string): Txn[] {
  const needle = (pattern ?? '').toUpperCase();
  if (!needle) return [];
  return (all ?? []).filter(t =>
    t.id !== excludeId && !t.isTransfer && (t.matchKey ?? '').toUpperCase().includes(needle));
}

/**
 * The offer to make after marking a row a transfer, or null if there is nothing
 * worth offering.
 *
 * Unlike a category offer, this fires even when there are no other matching rows:
 * a self-transfer that recurs monthly should be caught on the NEXT import, not
 * only retroactively.
 */
export function buildTransferOffer(txn: Txn, all: Txn[], rules: Rule[]): TransferOffer | null {
  const pattern = derivePattern(txn.matchKey);
  if (!pattern) return null;
  const known = (rules ?? []).some(r =>
    r.isTransfer && r.pattern.toUpperCase() === pattern.toUpperCase());
  if (known) return null;
  return { pattern, count: matchingNonTransfers(all, pattern, txn.id).length };
}

@Component({
  selector: 'app-money-transactions',
  templateUrl: './transactions.component.html',
  styleUrls: ['./transactions.component.scss'],
  standalone: false,
})
export class TransactionsComponent implements OnInit, OnDestroy {
  filters: TxnFilters = {
    month: '', accountId: '', categoryId: '', search: '',
    uncategorizedOnly: false, showTransfers: false, spendingOnly: false,
  };

  all: Txn[] = [];
  categories: Category[] = [];
  accounts: Account[] = [];
  months: string[] = [];
  backlog: { month: string; count: number }[] = [];
  selected = new Set<string>();
  bulkCategoryId = '';
  error = '';

  /** Accumulated across a review-queue burst; a rule offer per distinct pattern. */
  ruleOffers: RuleOffer[] = [];
  transferOffer: TransferOffer | null = null;

  private uid = '';
  private rules: Rule[] = [];
  private subs: Subscription[] = [];

  /**
   * One-shot guard on the month default. `filters.month` seeds from the query
   * string synchronously (see below) and, if a `month` param was supplied,
   * that guard flips true immediately so the data-driven default below never
   * gets a turn. Otherwise it flips true on the first RTDB emission that
   * carries months, seeding `filters.month` to the most recent one. Either
   * way, once true, a later background emission (a new import landing while
   * the screen is open) must never re-seed — the field is the user's own
   * choice afterward, and an explicit "All months" (`''`) is a real choice,
   * not an absent value, so this is a dedicated flag rather than a falsiness
   * check on filters.month.
   */
  private monthSeeded = false;

  constructor(
    private afAuth: AngularFireAuth,
    private money: MoneyService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    // Seeded from the route so the dashboard can hand off a category or the
    // uncategorized queue with its month already applied. A snapshot rather
    // than a subscription: this component is created fresh on navigation, and
    // the filters are user-owned state afterward — re-applying them on a later
    // emission would fight the user's own edits.
    const q = this.route.snapshot.queryParamMap;
    const month = q.get('month');
    const categoryId = q.get('categoryId');
    if (month) {
      this.filters.month = month;
      // A query-param month wins outright; the data-driven default must
      // never overwrite it, on this emission or any later one.
      this.monthSeeded = true;
    }
    if (categoryId) this.filters.categoryId = categoryId;
    if (q.get('uncategorized') === '1') this.filters.uncategorizedOnly = true;
    if (q.get('spending') === '1') this.filters.spendingOnly = true;

    this.subs.push(this.afAuth.authState.subscribe(user => {
      if (!user) return;
      this.uid = user.uid;
      this.subs.push(this.money.subscribeAll(this.uid).subscribe(data => {
        this.all = data.txns;
        this.categories = data.categories;
        this.accounts = data.accounts;
        this.rules = data.rules;
        this.months = monthsPresent(data.txns);
        this.backlog = uncategorizedByMonth(data.txns);

        // Default to the most recent month WITH data, not the calendar
        // month — imports are manual and monthly, so the current month
        // usually has none yet, which left the select blank and the table
        // empty. This still renders one month instead of all 1,231+ rows
        // (each with a 24-option select, ~30k DOM nodes and growing), and
        // "All months" stays one click away. Seeded once: an empty ledger
        // (no months at all) leaves the filter at '', the ordinary empty
        // state, and never gets a second chance to seed later.
        if (!this.monthSeeded && this.months.length) {
          this.filters.month = this.months[0];
          this.monthSeeded = true;
        }
      }));
    }));
  }

  // BOTH subscriptions: an unstored authState subscription outlives the
  // component and re-runs against a destroyed instance on the next auth event.
  ngOnDestroy(): void { this.subs.forEach(s => s.unsubscribe()); }

  trackMonth(_: number, m: string): string { return m; }
  trackAccount(_: number, a: Account): string { return a.id; }
  trackCategory(_: number, c: Category): string { return c.id; }
  trackBacklog(_: number, b: { month: string }): string { return b.month; }

  /**
   * A chip must open a list whose length equals the count it just showed.
   * uncategorizedByMonth counted with categoryId, accountId, search and
   * spendingOnly all absent, so this has to put the screen into exactly that
   * state rather than layering onto whatever filters a prior navigation left
   * behind — e.g. the dashboard's donut drill-through arrives here with
   * categoryId AND spending=1 already set, and uncategorizedOnly plus a
   * lingering categoryId are mutually exclusive, so the chip would open empty.
   */
  openBacklog(month: string): void {
    this.filters.month = month;
    this.filters.uncategorizedOnly = true;
    this.filters.categoryId = '';
    this.filters.accountId = '';
    this.filters.search = '';
    this.filters.spendingOnly = false;
  }

  chipActive(month: string): boolean {
    return this.filters.uncategorizedOnly && this.filters.month === month;
  }

  get visible(): Txn[] { return filterTxns(this.all, this.filters); }

  get reviewCount(): number {
    return this.all.filter(isUncategorized).length;
  }

  categoryName(id: string | null): string {
    return this.categories.find(c => c.id === id)?.name ?? '';
  }

  trackById(_: number, t: Txn): string { return t.id; }
  trackOfferPattern(_: number, o: RuleOffer): string { return o.pattern.toUpperCase(); }

  toggle(id: string): void {
    this.selected.has(id) ? this.selected.delete(id) : this.selected.add(id);
  }

  /**
   * Every template-invoked async handler routes through this so a rejected
   * write surfaces as a visible message instead of an unhandled rejection the
   * user never sees (e.g. a categorization that silently didn't save).
   */
  private async safe(action: () => Promise<void>): Promise<void> {
    try {
      await action();
    } catch (e) {
      this.error = (e as Error).message;
    }
  }

  async setCategory(txn: Txn, categoryId: string): Promise<void> {
    await this.safe(async () => {
      const id = categoryId || null;
      await this.money.updateTxn(this.uid, txn.id, { categoryId: id });

      if (!id) {
        // Clear only the offer for this transaction's own merchant, not the whole stack.
        const pattern = derivePattern(txn.matchKey);
        this.ruleOffers = this.ruleOffers.filter(o => o.pattern.toUpperCase() !== pattern.toUpperCase());
        return;
      }

      // The learning loop: offer to remember this merchant. `this.all` is the
      // last live snapshot — buildRuleOffer excludes txn.id so it never counts
      // the row we just categorized as still needing one.
      const offer = buildRuleOffer(txn, id, this.all, this.rules);
      if (!offer) return;
      this.ruleOffers = upsertOffer(this.ruleOffers, offer);
    });
  }

  async acceptRuleOffer(offer: RuleOffer): Promise<void> {
    await this.safe(async () => {
      await this.money.addRule(this.uid, {
        match: 'contains', pattern: offer.pattern, categoryId: offer.categoryId, isTransfer: false,
        priority: 100, hitCount: 0, createdAt: Date.now(),
      });

      // Recompute from the current live data rather than trusting the count
      // captured when the offer was built — it may be stale by now.
      const patches: Record<string, Partial<Txn>> = {};
      matchingUncategorized(this.all, offer.pattern)
        .forEach(t => { patches[t.id] = { categoryId: offer.categoryId }; });

      if (Object.keys(patches).length) await this.money.updateTxns(this.uid, patches);
      this.ruleOffers = this.ruleOffers.filter(o => o.pattern.toUpperCase() !== offer.pattern.toUpperCase());
    });
  }

  dismissRuleOffer(offer: RuleOffer): void {
    this.ruleOffers = this.ruleOffers.filter(o => o.pattern.toUpperCase() !== offer.pattern.toUpperCase());
  }

  async applyBulk(): Promise<void> {
    if (!this.bulkCategoryId || this.selected.size === 0) return;
    await this.safe(async () => {
      // A selected id may no longer exist (deleted/undone in another tab).
      // RTDB's update() has no existence check, so writing a patch for a
      // dead id would create a ghost node with a categoryId but no date —
      // one that crashes filterTxns/monthsPresent on the next emission.
      const liveIds = new Set(this.all.map(t => t.id));
      const patches: Record<string, Partial<Txn>> = {};
      this.selected.forEach(id => {
        if (liveIds.has(id)) patches[id] = { categoryId: this.bulkCategoryId };
      });
      if (Object.keys(patches).length) await this.money.updateTxns(this.uid, patches);
      this.selected.clear();
      this.bulkCategoryId = '';
    });
  }

  async toggleTransfer(txn: Txn): Promise<void> {
    await this.safe(async () => {
      const next = !txn.isTransfer;
      await this.money.updateTxn(this.uid, txn.id, { isTransfer: next });
      this.transferOffer = next ? buildTransferOffer(txn, this.all, this.rules) : null;
    });
  }

  async acceptTransferOffer(): Promise<void> {
    const offer = this.transferOffer;
    if (!offer) return;
    await this.safe(async () => {
      await this.money.addRule(this.uid, {
        match: 'contains', pattern: offer.pattern, categoryId: null, isTransfer: true,
        priority: 50, hitCount: 0, createdAt: Date.now(),
      });

      // Recompute from the current live data rather than trusting the count
      // captured when the offer was built — it may be stale by now.
      const patches: Record<string, Partial<Txn>> = {};
      matchingNonTransfers(this.all, offer.pattern)
        .forEach(t => { patches[t.id] = { isTransfer: true }; });

      if (Object.keys(patches).length) await this.money.updateTxns(this.uid, patches);
      this.transferOffer = null;
    });
  }

  dismissTransferOffer(): void {
    this.transferOffer = null;
  }

  async toggleExcluded(txn: Txn): Promise<void> {
    await this.safe(() => this.money.updateTxn(this.uid, txn.id, { excluded: !txn.excluded }));
  }

  /**
   * Secondary path onto the Recurring screen's manual list: mark this row's
   * merchant a bill by hand. Uses the detector's own recurringKey so this
   * screen and the Recurring screen never disagree about what key a
   * merchant groups under.
   */
  async markRecurring(txn: Txn): Promise<void> {
    await this.safe(async () => {
      const direction: Direction = txn.amount < 0 ? 'expense' : 'income';
      const key = recurringKey(txn.matchKey, direction, txn.description);
      await this.money.setRecurringState(this.uid, key, 'pinned');
    });
  }
}
